/**
 * PageErrorBoundary — catches unhandled render errors in the routed page
 * content (MainLayout's <Outlet/>) and offers "Report this issue" via the
 * support widget instead of a blank white screen. Deliberately scoped to
 * just the page content, not the whole app shell: Header/Sidebar/SupportWidget
 * stay mounted outside it, so a crash in one page doesn't take down
 * navigation or the ability to actually report the crash.
 *
 * Error boundaries must be class components (no hooks equivalent yet) —
 * the fallback itself is a function component so it can reach the support
 * store via useSupportWidgetStore.
 */

import { Component, type ReactNode } from 'react';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import { useSupportWidgetStore } from '../../stores/supportWidgetStore';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[4]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    width: 32px;
    height: 32px;
    color: ${({ theme }) => theme.colors.status.error.text};
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Message = styled.p`
  margin: 0;
  max-width: 480px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

/** Short, stable-enough reference the user can quote to support — not a full stack trace. */
function makeErrorRef(error: Error): string {
  const stamp = Date.now().toString(36);
  const nameHash = (error.name || 'Error').slice(0, 3).toUpperCase();
  return `${nameHash}-${stamp}`;
}

function ErrorFallback({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const openReportIssue = useSupportWidgetStore((s) => s.openReportIssue);
  const errorRef = makeErrorRef(error);

  return (
    <Wrapper role="alert">
      <AlertTriangle aria-hidden="true" />
      <Title>Something went wrong</Title>
      <Message>
        This page hit an unexpected error. You can try again, or report it and we&apos;ll take a
        look — reference <strong>{errorRef}</strong>.
      </Message>
      <Actions>
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
        <Button
          variant="primary"
          onClick={() => openReportIssue({ errorRef, subject: `Unexpected error (${errorRef})` })}
        >
          Report this issue
        </Button>
      </Actions>
    </Wrapper>
  );
}

interface PageErrorBoundaryState {
  error: Error | null;
}

export class PageErrorBoundary extends Component<{ children: ReactNode }, PageErrorBoundaryState> {
  state: PageErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }): void {
    // No centralized error-reporting service wired up yet — console keeps this
    // visible in dev/staging without silently swallowing the crash.
    console.error('Unhandled render error', error, info.componentStack);
  }

  // Reset lets a route change (Sidebar nav, retry button) attempt a fresh render
  // instead of staying stuck on the fallback until a full page reload.
  private handleRetry = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}

export default PageErrorBoundary;
